
import { useState, useEffect } from 'react';
import AppLayout from '../../components/feature/AppLayout';
import { supabase, Client, Interaction } from '../../lib/supabase';
import MetricCards from './components/MetricCards';
import ActivityBars from './components/ActivityBars';
import DistributionCharts from './components/DistributionCharts';
import TopCreatorsAndInteractions from './components/TopCreatorsAndInteractions';
import MonthlyEvolution from './components/MonthlyEvolution';

export type GmvPeriod = '7d' | '14d' | '28d' | '30d';

const periodOptions: { value: GmvPeriod; label: string }[] = [
  { value: '7d', label: '7 dias' },
  { value: '14d', label: '14 dias' },
  { value: '28d', label: '28 dias' },
  { value: '30d', label: '30 dias' },
];

export function getGmvField(period: GmvPeriod): string {
  const map: Record<GmvPeriod, string> = {
    '7d': 'gmv_interno_7d',
    '14d': 'gmv_interno_14d',
    '28d': 'gmv_interno_28d',
    '30d': 'gmv_interno_30d',
  };
  return map[period];
}

export default function MetricasPage() {
  const [clients, setClients] = useState<Client[]>([]);
  const [interactions, setInteractions] = useState<(Interaction & { client?: Client })[]>([]);
  const [loading, setLoading] = useState(true);
  const [gmvPeriod, setGmvPeriod] = useState<GmvPeriod>('30d');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [clientsRes, interactionsRes] = await Promise.all([
        supabase.from('clients').select('*').order('created_at', { ascending: false }),
        supabase.from('interactions').select('*, clients(*)').order('date', { ascending: false }),
      ]);

      if (clientsRes.data) setClients(clientsRes.data);
      if (interactionsRes.data) {
        const mapped = interactionsRes.data.map((i: any) => ({
          ...i,
          client: i.clients,
        }));
        setInteractions(mapped);
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-3 border-[#5de0e6] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="p-6 space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Métricas</h1>
            <p className="text-sm text-gray-400 mt-0.5">Visão geral do desempenho dos creators</p>
          </div>
          {/* Filtro de período GMV */}
          <div className="flex items-center bg-gray-100 rounded-full p-1 gap-0.5">
            {periodOptions.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setGmvPeriod(opt.value)}
                className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all whitespace-nowrap cursor-pointer ${
                  gmvPeriod === opt.value
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Cards principais */}
        <MetricCards clients={clients} gmvPeriod={gmvPeriod} />

        {/* Barras de atividade */}
        <ActivityBars clients={clients} totalInteractions={interactions.length} />

        {/* Distribuição */}
        <DistributionCharts clients={clients} />

        {/* Top Creators + Interações Recentes */}
        <TopCreatorsAndInteractions clients={clients} interactions={interactions} gmvPeriod={gmvPeriod} />

        {/* Evolução Mensal */}
        <MonthlyEvolution clients={clients} gmvPeriod={gmvPeriod} />
      </div>
    </AppLayout>
  );
}
